import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    form: {
        width: '100%',
    }
}));

const Reports = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [reports, setReports] = useState({
        max_age: 0,
        max_size: 100,
        size_range_max: 10000,
        size_range_min: 1,
    });
    const [changes, setChanges] = useState(false);
    const [original, setOriginal] = useState({});
    const reportForm = useValidate({});
    const reportsConfigPerms = Auth.permissions.get('ReportsConfig');

    useEffect(() => {
        if (systemSettings && systemSettings.reports) {
            const r = _cloneDeep(systemSettings.reports);
            setReports(r);
            setOriginal(systemSettings.reports);
        }
    }, [systemSettings]);

    useEffect(() => {
        setChanges(_isEqual(reports, original));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reports]);

    const handleMaxAge = (e, validID, valid) => {
        reportForm.set({ ...reportForm.get(), [validID]: valid });

        setReports({
            ...reports,
            max_age: isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value),
        });
    };

    const handleMaxSize = (e, validID, valid) => {
        reportForm.set({ ...reportForm.get(), [validID]: valid });

        setReports({
            ...reports,
            max_size: isNaN(parseInt(e.target.value)) ? '' : parseInt(e.target.value),
        });
    };

    const handleSave = () => {
        updateSystemSettings({ reports });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    return (
        <Grid container spacing={5}>
            <Grid item lg={6}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Box>
                            <Typography variant="h6" className={classes.labelMargin}>Maximum Age</Typography>
                            <FormControl className={classes.form}>
                                <Validator
                                    disabled={!reportsConfigPerms.write}
                                    valueType="range"
                                    errorText="Max age must be within 0 and 365 days"
                                    fullWidth
                                    aria-label="max age"
                                    type="number"
                                    value={reports.max_age}
                                    onChange={handleMaxAge}
                                    inputProps={{
                                        min: 0,
                                        max: 365,
                                    }}
                                    endAdornment={<InputAdornment position="end">Days</InputAdornment>}
                                />
                                <FormHelperText>Maximum retention age for reports, in days. Set to 0 (zero) to disable this functionality.</FormHelperText>
                            </FormControl>
                        </Box>
                        <Box mt={3}>
                            <Typography variant="h6" className={classes.labelMargin}>Maximum Size</Typography>
                            <FormControl className={classes.form}>
                                <Validator
                                    disabled={!reportsConfigPerms.write}
                                    valueType="range"
                                    errorText="Max size must bet within 1 and 10000 MB"
                                    fullWidth
                                    aria-label="max size"
                                    type="number"
                                    value={reports.max_size}
                                    onChange={handleMaxSize}
                                    inputProps={{
                                        min: reports.size_range_min || 1,
                                        max: reports.size_range_max || 10000,
                                    }}
                                    endAdornment={<InputAdornment position="end">MB</InputAdornment>}
                                />
                                <FormHelperText>Maximum total size for stored reports, in MB. Valid values are 1-10,000.</FormHelperText>
                            </FormControl>
                        </Box>
                        {reportsConfigPerms.write && <Box mt={3} display="flex" justifyContent="flex-end">
                            <Box><Button variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button></Box>
                            <Box ml={2}><Button
                                variant="contained"
                                color="primary"
                                aria-label="save"
                                onClick={handleSave}
                                disabled={!(checkValidity(reportForm.get()) && !changes)}
                            >
                                Save
                            </Button></Box>
                        </Box>}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Reports;
